import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { MolecularBg } from "../components/MolecularBg";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

export const StacksListPage = () => {
    const [stacks, setStacks] = useState([]);
    useEffect(() => { api.get("/stacks").then((r) => setStacks(r.data)); }, []);

    return (
        <div className="pt-24 pb-16 min-h-screen relative" data-testid="stacks-list-page">
            <MolecularBg intensity="low" />
            <div className="container-x relative z-10">
                <span className="eyebrow">// Curated Protocols</span>
                <h1 className="font-display italic text-4xl md:text-5xl font-black text-white tracking-tighter mt-1 mb-3 uppercase">
                    Research Stacks
                </h1>
                <p className="text-[var(--text-secondary)] mb-6 max-w-2xl">
                    Pre-curated peptide combinations for synergistic research protocols, built by our lab team
                    around established multi-pathway research.
                </p>
                <Link to="/stacks/builder" className="btn-neon mb-10 inline-flex" data-testid="builder-cta">
                    Build Your Own Stack <ArrowRight size={14} />
                </Link>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {stacks.map((s) => (
                        <Link
                            key={s.slug}
                            to={`/stacks/${s.slug}`}
                            className="glass-elevated rounded-2xl p-6 hover:border-[rgba(233,188,11,0.4)] transition-all group"
                            data-testid={`stack-card-${s.slug}`}
                        >
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--gold)] mb-3 block">{s.tagline}</span>
                            <h3 className="font-display italic uppercase font-bold text-white text-xl mb-3">{s.name}</h3>
                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">{s.description}</p>
                            <div className="flex items-center gap-2 mb-4">
                                {s.products.map((p) => (
                                    <div key={p.slug} className="w-16 h-11 rounded-lg overflow-hidden">
                                        <img src={p.image} alt={p.name} className="w-full h-full object-contain" />
                                    </div>
                                ))}
                            </div>
                            <div className="flex items-center justify-between pt-3 border-t border-white/5">
                                <span className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                    {s.products.length} compounds
                                </span>
                                <span className="font-display font-bold text-[var(--gold)] text-lg">A${s.total_price.toFixed(2)}</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const StackDetailPage = () => {
    const { slug } = useParams();
    const { addItem } = useCart();
    const [stack, setStack] = useState(null);
    const [added, setAdded] = useState(false);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setStack(null);
        setNotFound(false);
        api.get(`/stacks/${slug}`)
            .then((r) => setStack(r.data))
            .catch(() => setNotFound(true));
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [slug]);

    if (notFound) {
        return (
            <div className="pt-32 pb-24 container-x text-center" data-testid="stack-not-found">
                <div className="max-w-md mx-auto glass-elevated rounded-2xl p-8">
                    <h1 className="font-display text-2xl font-black text-white mb-2">Stack not found</h1>
                    <p className="text-sm text-[var(--text-secondary)] mb-5">
                        This stack may have been renamed or removed. Browse all available stacks below.
                    </p>
                    <Link to="/stacks" className="btn-solid inline-flex">Browse all stacks</Link>
                </div>
            </div>
        );
    }
    if (!stack) return <div className="pt-32 text-center text-[var(--text-secondary)]">Loading...</div>;

    const addStack = () => {
        let addedCount = 0;
        let skipped = 0;
        stack.products.forEach((p) => {
            const res = addItem(p, 1);
            if (res && res.ok === false) skipped += 1;
            else addedCount += 1;
        });
        setAdded(true);
        if (addedCount > 0 && skipped === 0) {
            toast.success(`${stack.name} added to cart (${addedCount} compounds)`);
        } else if (addedCount > 0) {
            toast.success(`${addedCount} added — ${skipped} skipped (out of stock)`);
        } else {
            toast.error("All compounds in this stack are currently out of stock");
        }
        setTimeout(() => setAdded(false), 1500);
    };

    return (
        <div className="pt-24 pb-20 min-h-screen relative" data-testid="stack-detail-page">
            <MolecularBg intensity="medium" />
            <div className="container-x relative z-10">
                <div className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] mb-6">
                    <Link to="/" className="hover:text-[var(--gold)]">Home</Link> /{" "}
                    <Link to="/stacks" className="hover:text-[var(--gold)]">Stacks</Link> /{" "}
                    <span className="text-white">{stack.name}</span>
                </div>

                <div className="grid lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3">
                        <span className="eyebrow">// {stack.tagline}</span>
                        <h1 className="font-display italic text-4xl md:text-5xl font-black text-white tracking-tighter mt-1 mb-4 uppercase">{stack.name}</h1>
                        <p className="text-[var(--text-secondary)] leading-relaxed mb-6">{stack.description}</p>

                        <h2 className="font-display italic uppercase text-lg font-bold text-white mt-8 mb-3">Compounds in this stack</h2>
                        <div className="space-y-3">
                            {stack.products.map((p) => (
                                <Link key={p.slug} to={`/product/${p.slug}`} className="glass rounded-xl p-4 flex items-center gap-4 hover:border-[rgba(233,188,11,0.3)] transition-all group" data-testid={`stack-product-${p.slug}`}>
                                    <div className="w-24 shrink-0">
                                        <img src={p.image} alt={p.name} className="w-full h-16 object-contain rounded-lg" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-display italic uppercase font-bold text-white">{p.name}</h3>
                                        <p className="text-xs text-[var(--text-secondary)] line-clamp-1 mt-1">{p.short_description}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <div className="font-display font-bold text-white">A${p.price.toFixed(2)}</div>
                                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{p.dosage}</div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="glass-elevated rounded-2xl p-6 neon-border-cyan sticky top-24" data-testid="stack-summary">
                            <h3 className="font-display italic uppercase font-bold text-white mb-1">Stack Total</h3>
                            <div className="font-display text-4xl font-black text-[var(--gold)] mb-4">A${stack.total_price.toFixed(2)}</div>
                            <ul className="space-y-2 mb-5">
                                {stack.products.map((p) => (
                                    <li key={p.slug} className="flex items-center justify-between text-sm">
                                        <span className="text-[var(--text-secondary)]">{p.name}</span>
                                        <span className="font-mono text-white">A${p.price.toFixed(2)}</span>
                                    </li>
                                ))}
                            </ul>
                            <button onClick={addStack} className="btn-solid w-full pulse-glow" data-testid="add-stack-button">
                                {added ? <><Check size={14} /> Added</> : <><ShoppingCart size={14} /> Add Stack to Cart</>}
                            </button>
                            <p className="text-[10px] text-[var(--text-muted)] mt-3 text-center font-mono uppercase tracking-widest">
                                For research use only · Not for human consumption
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
